import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "tm_pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

function isMobileUA() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPod|iPad|Mobile/i.test(navigator.userAgent);
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
  return iOS && webkit && notChrome;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const then = parseInt(raw, 10);
    if (!Number.isFinite(then)) return false;
    const days = (Date.now() - then) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function PwaInstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosVariant, setIosVariant] = useState(false);

  useEffect(() => {
    if (!isMobileUA() || isStandalone() || recentlyDismissed()) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari never fires beforeinstallprompt — show manual instructions.
    let iosTimer;
    if (isIosSafari()) {
      iosTimer = setTimeout(() => {
        setIosVariant(true);
        setVisible(true);
      }, 1200);
    }

    const onInstalled = () => {
      setVisible(false);
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  const install = async () => {
    if (!deferredEvt) return;
    deferredEvt.prompt();
    try {
      await deferredEvt.userChoice;
    } catch {}
    setDeferredEvt(null);
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 z-[60] md:hidden"
    >
      <div className="rounded-xl border border-[#E8E1D6] bg-[#FFFFFF] shadow-2xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#0F6B6B] flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-[#FFFFFF]" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[#2A2623] font-semibold text-sm">
            Install TrainMate
          </div>
          {iosVariant ? (
            <div className="text-[#2A2623] text-xs mt-1 leading-relaxed flex items-center gap-1 flex-wrap">
              Tap
              <Share className="w-3.5 h-3.5 inline mx-0.5" />
              <span className="font-medium text-[#2A2623]">Share</span>
              <span>then</span>
              <span className="font-medium text-[#2A2623]">Add to Home Screen</span>
            </div>
          ) : (
            <div className="text-[#2A2623] text-xs mt-1 leading-relaxed">
              Add it to your home screen. No browser bar, opens like a real app.
            </div>
          )}
          {!iosVariant && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#0F6B6B] text-[#FFFFFF] text-xs font-bold uppercase tracking-wider px-3 py-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={3} />
              Install app
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss install prompt"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          className="text-[#6B635C] hover:text-[#2A2623] p-1 -mr-1 -mt-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
